import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Receipt,
  Ban,
  Eye
} from "lucide-react";
import {
  getReceiptHtmlUrl,
  getReceiptHtmlUrlPortal
} from "../../api/fees/feeApi";
import {
  getReceipts,
  getReceiptsPortal,
  getReceiptById,
  getReceiptByIdPortal,
  voidReceipt,
  voidReceiptPortal
} from "../../api/fees/feeApi";
import {
  getAllAcademicYears,
  getAllAcademicYearsPortal
} from "../../api/academicyear/academicYearApi";
import FeeReceiptModal from "../../components/fees/FeeReceiptModal";
import {
  Button,
  Input,
  Select,
  Textarea,
  Badge,
  Table,
  Modal,
  Card,
  CardContent,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const ReceiptsList = ({ isPortal = false, canVoid = false }) => {
  const [receipts, setReceipts] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [filters, setFilters] = useState({
    academicYearId: "",
    fromDate: "",
    toDate: "",
    isVoided: "false",
  });
  const [loading, setLoading] = useState(true);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [voidId, setVoidId] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [voidLoading, setVoidLoading] = useState(false);

  const toast = useToast();

  useEffect(() => {
    (isPortal ? getAllAcademicYearsPortal() : getAllAcademicYears())
      .then((r) => {
        const years = r?.data || r || [];
        setAcademicYears(Array.isArray(years) ? years : []);
      })
      .catch(() => {});
  }, [isPortal]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const active = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "")
      );
      const res = isPortal ? await getReceiptsPortal(active) : await getReceipts(active);
      setReceipts(res.data || res || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load receipts.");
    } finally {
      setLoading(false);
    }
  }, [filters, isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const viewReceipt = async (id) => {
    try {
      const res = isPortal ? await getReceiptByIdPortal(id) : await getReceiptById(id);
      setReceiptData(res.data || res);
      setReceiptOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load receipt.");
    }
  };

  const submitVoid = async (e) => {
    e.preventDefault();
    if (!voidReason.trim()) return;
    setVoidLoading(true);
    try {
      if (isPortal) await voidReceiptPortal(voidId, { reason: voidReason.trim() });
      else await voidReceipt(voidId, { reason: voidReason.trim() });
      toast.success("Receipt voided successfully.");
      setVoidId(null);
      setVoidReason("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to void receipt.");
    } finally {
      setVoidLoading(false);
    }
  };

  const studentLabel = (r) => {
    const s = r.studentId;
    if (!s || typeof s !== "object") return "—";
    return (
      [s.firstName, s.middleName, s.lastName].filter(Boolean).join(" ") ||
      s.name ||
      s.admissionNo ||
      "—"
    );
  };

  const yearOptions = useMemo(
    () => [
      { label: "All Academic Years", value: "" },
      ...academicYears.map((y) => ({
        label: y.yearName || y.name || y._id,
        value: y._id,
      })),
    ],
    [academicYears]
  );

  const voidedOptions = [
    { label: "Active Only", value: "false" },
    { label: "Voided Only", value: "true" },
    { label: "All Receipts", value: "" },
  ];

  const columns = [
    {
      key: "receiptNo",
      header: "Receipt No.",
      render: (r) => (
        <span className="font-medium text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2">
          {r.receiptNo}
          {r.isVoided && (
            <Badge variant="danger" size="sm">
              Voided
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: "student",
      header: "Student",
      render: (r) => studentLabel(r),
    },
    {
      key: "date",
      header: "Payment Date",
      render: (r) =>
        r.paymentDate
          ? new Date(r.paymentDate).toLocaleDateString("en-IN")
          : "—",
    },
    {
      key: "amount",
      header: "Amount",
      render: (r) => (
        <span className="font-semibold text-neutral-900 dark:text-neutral-100">
          ₹{Number(r.totalPaid || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: "mode",
      header: "Mode",
      render: (r) => r.paymentMode || "—",
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="xs"
            onClick={() => viewReceipt(r._id)}
            leftIcon={<Eye className="h-3.5 w-3.5" />}
          >
            View
          </Button>
          {canVoid && !r.isVoided && (
            <Button
              variant="ghost"
              size="xs"
              className="text-danger-600 hover:bg-danger-50 hover:text-danger-700"
              onClick={() => {
                setVoidId(r._id);
                setVoidReason("");
              }}
              leftIcon={<Ban className="h-3.5 w-3.5" />}
            >
              Void
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Fee Receipts</h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          View payment history and void receipts when required.
        </p>
      </div>

      
      

      {/* Filter Toolbar Card */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Select
              label="Academic Year"
              options={yearOptions}
              value={filters.academicYearId}
              onChange={(value) =>
                setFilters((p) => ({ ...p, academicYearId: value }))
              }
            />
            <Input
              label="From Date"
              type="date"
              value={filters.fromDate}
              onChange={(e) =>
                setFilters((p) => ({ ...p, fromDate: e.target.value }))
              }
            />
            <Input
              label="To Date"
              type="date"
              value={filters.toDate}
              onChange={(e) =>
                setFilters((p) => ({ ...p, toDate: e.target.value }))
              }
            />
            <Select
              label="Status"
              options={voidedOptions}
              value={filters.isVoided}
              onChange={(value) =>
                setFilters((p) => ({ ...p, isVoided: value }))
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Receipts Table */}
      {loading ? (
        <Card>
          <CardContent className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex gap-4">
                <Skeleton height={16} width="20%" />
                <Skeleton height={16} width="25%" />
                <Skeleton height={16} width="15%" />
                <Skeleton height={16} width="20%" />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table
              columns={columns}
              data={receipts}
              emptyState={
                <EmptyState
                  icon={Receipt}
                  title="No receipts found"
                  description="Adjust filters or collect a payment to generate a receipt."
                />
              }
            />
          </CardContent>
        </Card>
      )}

      {/* Void Receipt Modal */}
      <Modal
        isOpen={!!voidId}
        onClose={() => setVoidId(null)}
        title="Void Receipt"
        footer={
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 w-full">
            <Button
              variant="secondary"
              onClick={() => setVoidId(null)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              isLoading={voidLoading}
              onClick={submitVoid}
              className="w-full sm:w-auto"
            >
              Confirm Void
            </Button>
          </div>
        }
      >
        <div className="pt-1">
          <Textarea
            label="Reason *"
            placeholder="Enter the reason for voiding this receipt…"
            required
            rows={3}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
          />
        </div>
      </Modal>

      {/* Fee Receipt View Modal */}
      <FeeReceiptModal
        isOpen={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        receipt={receiptData}
        isPortal={isPortal}
      />
    </div>
  );
};

export default ReceiptsList;